// Achievement unlock toast — slides in from the right, holds, slides back out.
// Queues multiple unlocks so they appear one after another.
import Phaser from "phaser";
import { AchievementDef, TIER_DEFAULTS } from "../systems/achievementDefs";
import { achievements } from "../systems/Achievements";
import { GAME_WIDTH, TOP_BAR_HEIGHT } from "../config";
import { THEME, TIER_STYLE, panel } from "./theme";

const TOAST_W  = 420;
const TOAST_H  = 84;
const TOAST_X_SHOW = GAME_WIDTH - TOAST_W - 14;
const TOAST_X_HIDE = GAME_WIDTH + 4;
const TOAST_Y  = TOP_BAR_HEIGHT + 10;

const SLIDE_IN_MS  = 300;
const HOLD_MS      = 2800;
const SLIDE_OUT_MS = 340;

interface QueueEntry { id: string; def: AchievementDef }

class AchievementToastManager {
  private queue: QueueEntry[] = [];
  private running = false;
  private scene: Phaser.Scene | null = null;
  private listenerRegistered = false;

  /** Bind toasts to the given scene; safe to call on every scene create().
   *  Registers the achievements unlock listener exactly once (achievements.on
   *  has no unsubscribe, and Phaser reuses scene instances across restarts). */
  attach(scene: Phaser.Scene): void {
    this.scene = scene;
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.scene === scene) this.scene = null;
      // A toast mid-flight dies with the scene; allow the next attach to resume.
      this.running = false;
    });
    if (!this.listenerRegistered) {
      this.listenerRegistered = true;
      achievements.on((id, def) => this.enqueue(id, def));
    }
  }

  enqueue(id: string, def: AchievementDef): void {
    this.queue.push({ id, def });
    this.processQueue();
  }

  private processQueue(): void {
    if (this.running || this.queue.length === 0 || !this.scene) return;
    const entry = this.queue.shift()!;
    this.running = true;
    this.show(entry, () => {
      this.running = false;
      this.processQueue();
    });
  }

  private buildRewardLine(def: AchievementDef): string {
    const defaults = TIER_DEFAULTS[def.tier];
    const xp     = def.reward.xp           ?? defaults.xp           ?? 0;
    const sp     = def.reward.skillPoints   ?? defaults.skillPoints  ?? 0;
    const parts: string[] = [];
    if (xp > 0) parts.push(`+${xp.toLocaleString()} XP`);
    if (sp > 0) parts.push(`+${sp} Skill Point${sp > 1 ? "s" : ""}`);
    return parts.length > 0 ? parts.join("  ·  ") : "No bonus reward";
  }

  private show(entry: QueueEntry, onDone: () => void): void {
    const scene = this.scene!;
    const { def } = entry;
    const tier = TIER_STYLE[def.tier];
    const rewardLine = this.buildRewardLine(def);

    const c = scene.add.container(TOAST_X_HIDE, TOAST_Y).setDepth(THEME.depths.toast);

    // ── Parchment background ─────────────────────────────────────────────────
    const bg = panel(scene, 0, 0, TOAST_W, TOAST_H, "parchment");

    // ── Left tier-accent bar (3 px, full height, tier color) ─────────────────
    const accentBar = scene.add.rectangle(3, TOAST_H / 2, 3, TOAST_H - 8, tier.hex, 1)
      .setOrigin(0.5);

    // ── Badge circle ─────────────────────────────────────────────────────────
    const badgeCx = 48;
    const badgeCy = TOAST_H / 2;
    const badge = scene.add.arc(badgeCx, badgeCy, 24, 0, 360, false, tier.hex, 1)
      .setStrokeStyle(2, 0xffd66b, 0.8);

    const badgeLabel = scene.add.text(badgeCx, badgeCy, tier.numeral, {
      fontFamily: THEME.fonts.display,
      fontSize: "15px",
      color: THEME.colors.iron,
    }).setOrigin(0.5);

    // ── Thin vertical gold divider ────────────────────────────────────────────
    const divider = scene.add.rectangle(80, TOAST_H / 2, 1, TOAST_H - 18, THEME.hex.goldDim, 0.45)
      .setOrigin(0.5);

    // ── Text ──────────────────────────────────────────────────────────────────
    const textX = 90;

    const headerLabel = scene.add.text(textX, 12, "ACHIEVEMENT UNLOCKED", {
      fontFamily: THEME.fonts.body,
      fontSize: "11px",
      color: THEME.colors.parchmentDim,
      letterSpacing: 2,
    }).setOrigin(0, 0);

    const nameLabel = scene.add.text(textX, 27, def.name, {
      fontFamily: THEME.fonts.display,
      fontSize: "17px",
      color: tier.nameColor,
      stroke: THEME.colors.ironDeep,
      strokeThickness: 3,
    }).setOrigin(0, 0);

    const rewardLabel = scene.add.text(textX, 53, rewardLine, {
      fontFamily: THEME.fonts.body,
      fontSize: "13px",
      color: THEME.colors.parchmentDim,
    }).setOrigin(0, 0);

    c.add([bg, accentBar, badge, badgeLabel, divider, headerLabel, nameLabel, rewardLabel]);

    // ── Slide in from right ──────────────────────────────────────────────────
    scene.tweens.add({
      targets: c,
      x: TOAST_X_SHOW,
      duration: SLIDE_IN_MS,
      ease: "Cubic.easeOut",
      onComplete: () => {
        scene.time.delayedCall(HOLD_MS, () => {
          // ── Slide out to right with fade ───────────────────────────────────
          scene.tweens.add({
            targets: c,
            x: TOAST_X_HIDE,
            alpha: 0,
            duration: SLIDE_OUT_MS,
            ease: "Cubic.easeIn",
            onComplete: () => {
              c.destroy();
              onDone();
            },
          });
        });
      },
    });
  }
}

export const achievementToast = new AchievementToastManager();
